<template>
  <div
    ref="container"
    v-click-outside="onOutsideClick"
    class="rounded-16 overflow-hidden absolute left-0 top-0 bg-elev-modal-bg select-none shadow-overlay z-20 min-w-[220px]"
  >
    <div
      class="p-2 flex gap-2 items-center justify-between"
    >
      <wui-icon
        name="confCall"
        class="text-primary"
      />
      <p class="text-heading-sb-16-22 text-fg-base-fg3">
        {{ t('common.callScheduled') }}
      </p>
      <wui-btn
        icon
        prepend-icon="closeRegularMdM"
        variant="neut"
        state="alpha"
        :size="32"
        @click="$emit('close')"
      />
    </div>

    <div class="p-6 space-y-6 border-y border-y-bor-divider">
      <p class="font-medium text-1316 text-center max-w-[313px]">
        {{ scheduleEventDisplayName }}
      </p>
      <p
        class="font-medium text-1316 text-center whitespace-pre-wrap max-w-[313px]"
        data-test="event-info-datetime"
      >
        {{ datetime }}
      </p>

      <div v-if="organizer">
        <p class="text-fg-base-fg22 mb-1 text-1316 text-center">
          {{ t('common.organizer') }}
        </p>
        <p class="font-medium text-center">
          {{ organizer }}
        </p>
      </div>

      <div v-if="participants.length">
        <p class="text-fg-base-fg22 mb-1 text-1316 text-center">
          {{ t('common.participants') }}
        </p>
        <users-avatars-group
          :contacts
        />
      </div>

      <div
        class="flex justify-center"
      >
        <wui-btn
          v-if="scheduleEvent && !isEventPassed"
          prepend-icon="linkM"
          variant="brand"
          state="tonal"
          class="grow-0 shrink-0"
          @click="downloadInvitation"
        >
          {{ t('common.downloadInvitation') }}
        </wui-btn>
        <wui-btn
          v-else-if="matchedAppointmentEndEvent"
          variant="brand"
          state="tonal"
          class="grow-0 shrink-0"
          @click="openCallSummary"
        >
          {{ t('common.openCallSummary') }}
        </wui-btn>
      </div>
    </div>

    <div class="py-2 px-[34px] flex gap-2 justify-center">
      <wui-btn
        v-if="scheduleEvent?.location"
        data-test="copy-conference-link-btn"
        icon
        variant="brand"
        state="tonal"
        prepend-icon="linkM"
        :title="t('common.copyLink')"
        @click="copyConferenceLink"
      />

      <wui-btn
        v-if="scheduleEvent?.location && isChatMessagingUiEnabled"
        data-test="go-to-chat-btn"
        icon
        variant="brand"
        state="tonal"
        :title="t('common.goToChat')"
        prepend-icon="messageM"
        @click="goToChat"
      />

      <wui-btn
        v-if="canDeleteOrEditEvent"
        data-test="edit-schedule-btn"
        icon
        variant="brand"
        state="tonal"
        :title="t('common.editEvent')"
        prepend-icon="editM"
        @click="openScheduleEditDialog"
      />

      <wui-btn
        v-if="canDeleteOrEditEvent"
        icon
        variant="negative"
        state="tonal"
        :title="t('common.remove')"
        prepend-icon="trashM"
        @click="askToDeleteEvent"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WuiIcon, WuiBtn, useRenderNotifier, useDialog, ClickOutside as vClickOutside } from '@wui/common-library'
import { ConferenceType, HistoryMessageItem, MessageCallAppointmentEnd, MessageCallReceived } from '@wui/im'
import dayjs from 'dayjs'
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'


import { ScheduleEditDialog, ScheduleEditErrorDialog, ScheduleEditSuccessDialog } from '@/widgets/room-schedule-dialog'

import { ConfirmEventCancelDialog } from '@/features/confirm-event-cancel'
import { formatScheduleNotificationText, useScheduleDownloadIcs } from '@/features/schedule'

import { AppointmentStatistic, getCallEventText } from '@/entities/call-chat-event'
import { UsersAvatarsGroup, useStoreContacts } from '@/entities/contact'
import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import {
  SCHEDULE_EVENT_ID_SEPARATOR,
  getScheduleEventDisplayName,
  useCalendarAttendeeContacts,
  useRoomScheduleStore,
} from '@/entities/schedule'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { Contact } from '@/shared/types/api'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUserIfRoom } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

const { findByPServed } = useStoreRooms()

const props = defineProps<{
  eventId: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'ref', ref: HTMLDivElement): void
}>()

const container = ref<HTMLDivElement>()
const isClickOutsideEnabled = ref(false)

const enableClickOutside = async () => {
  isClickOutsideEnabled.value = false
  await nextTick()
  window.setTimeout(() => {
    isClickOutsideEnabled.value = true
  }, 0)
}

const onOutsideClick = () => {
  if (!isClickOutsideEnabled.value) {
    return
  }
  emit('close')
}

onMounted(async () => {
  if (container.value) {
    emit('ref', container.value)
  }

  await enableClickOutside()
})

onBeforeUnmount(() => {
  isClickOutsideEnabled.value = false
})

watch(() => props.eventId, () => {
  enableClickOutside()
})

const { t } = useLocalization()
const { isChatMessagingUiEnabled } = useUserFeatures()
const { currentUser } = useCurrentUser()

const room = computed(() => {
  return findByPServed(roomPServed.value!)
})

const canDeleteOrEditEvent = computed(() => {
  if (room.value?.mode === ConferenceType.MODERATED) {
    return userRole.value === 'Moderator'
  }
  return true
})

const isEventPassed = computed(() => {
  return dayjs(scheduleEvent.value?.start.date).isBefore(dayjs())
})

const datetime = computed(() => {
  if (!scheduleEvent.value) {
    return ''
  }

  return formatScheduleNotificationText(scheduleEvent.value)
})

const userRole = computed(() => {
  return room.value?.contacts?.find(contact => contact.id === currentUser.value?.imLogin)?.role
})

const roomScheduleStore = useRoomScheduleStore()
const scheduleEvent = computed(() => {
  const schedule = roomScheduleStore.findScheduleById(props.eventId)
  return schedule?.events?.[0]
})

const scheduleEventDisplayName = computed(() => {
  if (!scheduleEvent.value) {
    return ''
  }

  return getScheduleEventDisplayName(scheduleEvent.value.summary, t('common.withoutTitle'))
})

const organizer = computed(() => {
  return scheduleEvent.value?.organizer?.name || ''
})

const participants = computed(() => {
  return scheduleEvent.value?.attendees || []
})

const location = computed(() => {
  return scheduleEvent.value?.location || ''
})

const { notifySuccess, notifyError } = useRenderNotifier()
const copyConferenceLink = async () => {
  if (!location.value) {
    return
  }

  try {
    await navigator.clipboard.writeText(location.value)
    notifySuccess(t('common.linkCopiedToClipboard'))
  } catch {
    notifyError(t('common.linkCopyError'))
  }
}

const goToChat = () => {
  if (!roomPServed.value) {
    return
  }

  optionalPush(getChatRoute(fromPServedUserIfRoom(roomPServed.value)))
}

const { showDialog } = useDialog()
const { findRoomMessages, fetchAndAddHistory } = chatModel.useChatMessageStore()

const roomPServed = computed(() => {
  const eventIdParts = props.eventId.split(SCHEDULE_EVENT_ID_SEPARATOR)
  return eventIdParts.length === 2 ? eventIdParts[0] : ''
})

const scheduledEventUid = computed(() => {
  const eventIdParts = props.eventId.split(SCHEDULE_EVENT_ID_SEPARATOR)
  return eventIdParts.length === 2 ? eventIdParts[1] : ''
})

const getAppointmentEndEvents = (): HistoryMessageItem<MessageCallAppointmentEnd>[] => {
  if (!roomPServed.value) {
    return []
  }

  return findRoomMessages(roomPServed.value)
    .filter(event => (event.item as MessageCallReceived).event === 'appointment_end') as HistoryMessageItem<MessageCallAppointmentEnd>[]
}

const matchesScheduledAppointmentEnd = (event: HistoryMessageItem<MessageCallAppointmentEnd>) => {
  return event.item.conferenceStatistic?.appointmentPlanningGuid === scheduledEventUid.value
}

const findMatchingAppointmentEndEvent = (
  appointmentEndEvents: HistoryMessageItem<MessageCallAppointmentEnd>[],
): HistoryMessageItem<MessageCallAppointmentEnd> | null => {
  return [...appointmentEndEvents].reverse().find(matchesScheduledAppointmentEnd) ?? null
}

const getAppointmentEndEvent = (): HistoryMessageItem<MessageCallAppointmentEnd> | null => {
  return findMatchingAppointmentEndEvent(getAppointmentEndEvents())
}

const matchedAppointmentEndEvent = computed(() => getAppointmentEndEvent())

watch([isEventPassed, roomPServed, scheduledEventUid], async () => {
  if (!isEventPassed.value || !roomPServed.value || matchedAppointmentEndEvent.value) {
    return
  }

  try {
    await fetchAndAddHistory(roomPServed.value, 100)
  } catch (error) {
    console.error('Failed to prefetch chat history for call summary', error)
  }
}, { immediate: true })

const openCallSummary = async () => {
  let event = getAppointmentEndEvent()

  if (!event && roomPServed.value) {
    try {
      await fetchAndAddHistory(roomPServed.value, 100)
      event = getAppointmentEndEvent()
    } catch (error) {
      console.error('Failed to fetch chat history for call summary', error)
    }
  }

  if (!event) {
    notifyError(t('common.emptyData'))
    return
  }

  showDialog(AppointmentStatistic, {
    event,
    message: getCallEventText(event),
    isModal: true,
    hasOverlay: true,
  })
}

const askToDeleteEvent = () => {
  if (!scheduleEvent.value) {
    return
  }

  showDialog(ConfirmEventCancelDialog, {
    scheduledEventName: scheduleEventDisplayName.value,
    scheduledEventUid: scheduledEventUid.value!,
    pServed: roomPServed.value!,
  })
}

const openScheduleEditDialog = async () => {
  if (!scheduleEvent.value) {
    return
  }

  const response = await showDialog(
    ScheduleEditDialog,
    {
      eventId: props.eventId,
      scheduleEventName: scheduleEvent.value.summary,
      pServed: roomPServed.value ?? '',
      date: scheduleEvent.value.start.date,
      event: scheduleEvent.value,
    },
  ) as { error: Error } | { pServed: string, participants: string[], uid: string }

  if (typeof response === 'object') {
    if ('error' in response) {
      showDialog(ScheduleEditErrorDialog, {
        error: response.error,
        hasOverlay: true,
      })
    } else if ('pServed' in response) {
      showDialog(ScheduleEditSuccessDialog, {
        pServed: response.pServed,
        participantsNumbers: response.participants,
        hasOverlay: true,
        uid: response.uid,
      })
    }
  }
}

const contactsStore = useStoreContacts()
const { resolveAttendees } = useCalendarAttendeeContacts()
const contacts = ref<Contact[]>([])

watch(() => [contactsStore.allContacts.length, scheduleEvent.value], () => {
  contacts.value = resolveAttendees(participants.value)
}, { immediate: true })

watch(scheduleEvent, (n, o) => {
  if (!n && o) {
    emit('close')
  }
})

const { downloadIcs } = useScheduleDownloadIcs(scheduleEvent)
const downloadInvitation = () => {
  if (scheduleEvent.value && room.value) {
    downloadIcs({
      description: scheduleEvent.value.description!,
    })
  } else {
    downloadIcs()
  }
}

</script>
